import clsx from 'clsx'
import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react'
import { useStore } from '../../store/useStore'
import type { Interval, SymbolPrediction } from '../../types'
import { getSignal, SIGNAL_TEXT, SIGNAL_TEXT_COLOR, type Signal } from '../../utils/signal'

interface Props {
  symbol: string
  predictions: Record<string, SymbolPrediction>
  loadingPredictions: Record<string, boolean>
}

const INTERVALS: Interval[] = ['1h', '4h', '1d']
const INTERVAL_LABELS: Record<Interval, string> = { '1h': '1H足', '4h': '4H足', '1d': '1D足' }

function SignalIcon({ signal }: { signal: Signal }) {
  if (signal === 'up') return <TrendingUp size={16} className="text-trading-green" />
  if (signal === 'down') return <TrendingDown size={16} className="text-trading-red" />
  return <Minus size={16} className="text-trading-yellow" />
}

export default function MultiTimeframeSummary({ symbol, predictions, loadingPredictions }: Props) {
  const selectedInterval  = useStore((s) => s.selectedInterval)
  const setSelectedInterval = useStore((s) => s.setSelectedInterval)

  const rows = INTERVALS.map((iv) => ({
    interval: iv,
    prediction: predictions[`${symbol}_${iv}`] ?? null,
    loading: loadingPredictions[`${symbol}_${iv}`] ?? false,
  }))

  return (
    <div className="trading-card flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-trading-muted">
          1日後の予測比較 · マルチタイムフレーム
        </h3>
      </div>

      {/* 3-column grid */}
      <div className="grid grid-cols-3 gap-3">
        {rows.map(({ interval, prediction, loading }) => {
          const prob = prediction?.prediction.rise_probability ?? null
          const signal = prob !== null ? getSignal(prob) : null

          const isActive = interval === selectedInterval

          return (
            <div
              key={interval}
              onClick={() => setSelectedInterval(interval)}
              className={clsx(
                'flex flex-col items-center gap-1.5 rounded-md border p-3 cursor-pointer transition-all duration-150',
                isActive
                  ? 'bg-trading-blue/10 border-trading-blue/50 ring-1 ring-trading-blue/30'
                  : 'bg-trading-bg border-trading-border hover:border-trading-blue/30 hover:bg-trading-blue/5',
              )}
            >
              <span className={clsx(
                'text-[10px] font-bold uppercase tracking-widest',
                isActive ? 'text-trading-blue' : 'text-trading-muted',
              )}>
                {INTERVAL_LABELS[interval]}
              </span>

              {loading || (prob === null && !prediction) ? (
                <Loader2 size={18} className="animate-spin text-trading-muted/50 my-1" />
              ) : prob !== null ? (
                <>
                  <div className={clsx('text-2xl font-bold font-mono tabular-nums', SIGNAL_TEXT_COLOR[signal!])}>
                    {Math.round(prob * 100)}%
                  </div>
                  <div className="flex items-center gap-1">
                    <SignalIcon signal={signal!} />
                    <span className={clsx('text-[10px] font-bold', SIGNAL_TEXT_COLOR[signal!])}>
                      {SIGNAL_TEXT[signal!]}
                    </span>
                  </div>
                </>
              ) : (
                <span className="text-[10px] text-trading-muted">データなし</span>
              )}

              <span className="text-[9px] text-trading-muted/60">上昇確率</span>
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <p className="text-[9px] text-trading-muted/50 text-right">
        各時間足で24時間後（1日後）を予測
      </p>
    </div>
  )
}
